import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    inject,
    Input,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation
} from "@angular/core";
import { FormioPDFGenerator } from "@features/bb-formio/classes/pdf-generator/formio-pdf-generator";
import { FormDataResponse, FormIOData, FormIODataDraft } from "@features/bb-formio/dto/form-data";
import {
    FormDefinitionParameters,
    FormFields,
    FormFieldValues,
    FormIODefinition
} from "@features/bb-formio/dto/form-definition";
import { FormIODataService } from "@features/bb-formio/services/formio-data.service";
import { FormIODefinitionService } from "@features/bb-formio/services/formio-definition.service";
import { IReportGenerator } from "@features/pdf-report-generator/IReportGenerator";
import { IReportResource } from "@features/pdf-report-generator/IReportResource";
import { PDFLibrary } from "@features/pdf-report-generator/pdf-libraries";
import { ExtendedComponentSchema, FormioComponent, FormioOptions } from "@formio/angular";
import { LinkUserService, UserLogin, UserService } from "@main/users";
import { PrismService } from "../../prism.service";
import { FormIODataDraftService } from "@features/bb-formio/services/formio-data-draft.service";
import { BbImageUploadEvent } from "@features/bb-image-uploader";
import { ConfirmationService, MessageService } from "primeng/api";
import { HttpEventType } from "@angular/common/http";
import { Message } from "@bbwt/classes";
import { FileDetails, FilesUploadingResult, FilesUploadingStatus } from "@main/file-storage";
import { S3Service } from "@features/bb-formio/Providers/s3";
import { MUFStage } from "@features/bb-formio/dto/muf-stage";
import { TabState } from "../custom-components/tabs/interfaces";
import { IEpisode } from "@project/models/episodes/episode";
import { Location } from "@angular/common";
import { UserHelperService } from "@main/users/user-helper.service";
import { CoreRole, FormioRole } from "@main/roles/core-role";
import { FormioHtmlParserService } from "@features/bb-formio/classes/pdf-generator/formio-html-parser.service";
import { FormioHTMLData } from "@main/formio/components/formio-pdf/formio-pdf-generator.component";
import { EpisodeService } from "@project/episode/episode.service";
import { EpisodeStatus } from "@project/models/episodes/episode-status";
import { ShareFormDataDTO } from "@main/formio/dto/shareFormDataDTO";
import { FormioDataViewerService } from "@main/formio/services/formioDataViewer.service";
import { Router, UrlTree } from "@angular/router";
import { IHttpResponseHandlerSettings } from "@bbwt/modules/data-service";
import { FormioMiProExtraParameters } from "@features/bb-formio/classes/FormioMiProExtraParameters";
import { FormIORevisionService } from "@features/bb-formio/services/formio-revision.service";
import { FormRevisionDTO } from "@features/bb-formio/dto/form-revision";
import { SetUTCTimeZone } from "@bbwt/utils";
import { AppStorage } from "@bbwt/utils/app-storage";
import { NoteService } from "@project/note-shared/classes/note.service";
import { PrescriptionLinesService } from "@project/prescription-shared/classes/prescription-lines.service";
import {
    MultiUserFormAssociationsService
} from "@main/formio/multi-user-form/services/multi-user-form-associations.service";
import { CustomComponentTypes } from "@main/formio/components/data-insertion-dialog/customComponentTypes";
import { MultiUserFormAssociation } from "@main/formio/multi-user-form/dto/multi-user-form-associations.dto";
import { MultiUserFormAssociationLink } from "@main/formio/multi-user-form/dto/multi-user-form-association-links.dto";
import { PatientService } from "@main/app-layout/components/patient-switch/patient.service";
import { NavigationHistoryService } from "@mipro/services/navigation-history.service";
import { EntityId } from "@bbwt/interfaces";
import * as moment from "moment";
import { SelfRegistrationFormService } from "@project/self-registration/forms/self-registration-form.service";
import {
    FormIoShareDialogService,
    ShareFormDialogResultStatus
} from "@main/formio/components/formio-share-dialog/formio-share-dialog.service";
import { firstValueFrom } from "rxjs";

@Component({
    selector: "bb-form-renderer",
    templateUrl: "./bb-form-renderer.component.html",
    styleUrls: ["../../formio.styles.scss", "./bb-form-renderer.component.scss"],
    encapsulation: ViewEncapsulation.None
})
export class BbFormRendererComponent implements OnInit, AfterViewInit {
    public prism = inject(PrismService);
    public formIODefinitionService = inject(FormIODefinitionService);
    public formIODataService = inject(FormIODataService);
    public formIODataDraftService = inject(FormIODataDraftService);
    public fromIODataViewerService = inject(FormioDataViewerService);
    public formIORevisionService = inject(FormIORevisionService);
    public selfRegistrationFormService = inject(SelfRegistrationFormService);

    private multiUserFormAssociationsService = inject(MultiUserFormAssociationsService);
    private userService = inject(UserService);
    private userHelperService = inject(UserHelperService);
    private formIOFileUploaderService = inject(S3Service);
    private messageService = inject(MessageService);
    private location = inject(Location);
    private formioHtmlParserService = inject(FormioHtmlParserService);
    private episodeService = inject(EpisodeService);
    private noteService = inject(NoteService);
    private prescriptionLinesService = inject(PrescriptionLinesService);
    private router = inject(Router);
    private patientService = inject(PatientService);
    private linkUserService = inject(LinkUserService);
    private confirmationService = inject(ConfirmationService);
    private navigationHistoryService = inject(NavigationHistoryService);
    private formIoShareDialogService = inject(FormIoShareDialogService);

    public mobileFriendly = true;
    public formJson: any = {};
    public formDataJson: any = {};
    public formDataJsonOriginal: any = {};
    public formOptions: FormioOptions = {};

    @Input() anonymousSelfRegForm: boolean = false;
    @Input() selfRegOrgUrl: string;

    @Input() formioMiProExtraParameters: FormioMiProExtraParameters = {};
    @Input() isMultiUserForm = false;
    @Input() formDefId: string;
    @Input() formDataId: string;
    @Input() mufDefinitionId: string;
    @Input() readOnlyForm = false;
    @Input() shareOnComplete = false;
    @Input() extraData: string = "";
    @Input() requestId: string;
    @Input() revisionId: string;
    @Input() surveyId: string | number;
    @Input() mufStages: MUFStage[] = [];
    @Input() currentStageId: string | number = "";
    @Input() mufAssocId: string | number;
    @Input() mufAssoc: MultiUserFormAssociation;
    @Input() showBackButton: boolean = true;
    @Input() generatePdf: boolean = false;

    @Input() mufOrgId: string = null;
    @Input() mufUserId: string = null;

    @Input() restoreFormDraft = false;
    @Input() patientId: string = null;
    @Input() episodeMandatory: boolean = true;
    @Input() isLastStage = false;
    @Input() isDraft = false;

    @Input() walkInId: string = null;
    @Input() returnUrl: string = null;
    @Input() noteDraftId: string = null;
    @Input() isReadOnlyEpisode: boolean = false;
    @Input() useBackNavigationParams: boolean = false;
    @Input() goBackOnSubmit: boolean = true;

    @Output() readonly selectEpisodeBeforeSaving = new EventEmitter<void>();
    @Output() readonly episodeChanged = new EventEmitter<string>();
    @Output() readonly showExistingDraftMessage = new EventEmitter<boolean>();

    @Output() readonly initialPdfGenerated = new EventEmitter();
    @Output() readonly formDataSaved = new EventEmitter();

    public unsubmittedData: { submission: any, callback: any } = null;

    private clinicianFieldTypes: string[] = [];
    private autoPopulatedFieldTypes: string[] = [];
    private clinicianFieldComponents: ExtendedComponentSchema[] = [];

    refreshForm = new EventEmitter();
    dataSubmitted = false;
    sendingData = false;
    dataFilledAlready = false;
    invalidParameters = false;
    draftSendEnabled = true;
    draftTimeoutId: any = null;
    draftPromptPending = false;
    formDraft: FormIODataDraft;
    formDraftFromServer: FormIODataDraft;
    showImageUploadDialog = false;
    imageUploadKey = "";
    imagesKeysPerImageUploaderComponent = {};
    displayShareFileDialog = false;
    revision: FormRevisionDTO;
    shareFile: FileDetails;
    shareDto: ShareFormDataDTO;

    submitDraft = false;

    formDefinitionName = "";
    hasFileAttachments = false;
    generatingPdf = false;
    isExternalUser = false;

    maxSizePx = 1000;
    progress = 0;

    showCancelButton: boolean = false;

    showUpdateClinicianDataButton: boolean = false;

    formLoading = false;

    submission: any;
    callback: any;

    private _episode: IEpisode = null;

    @Input() set episode(value: IEpisode) {
        this._episode = value;
        this.callback?.({silent: true}, null);

        if (this.formDraft && this.formDraft.episodeId !== value?.id) {
            this.formDraft.episodeId = value?.id;
            this.scheduleDraftSend();
        }
    }

    get episode(): IEpisode {
        return this._episode;
    }

    get generatePdfButtonLabel() {
        return this.generatingPdf ? "Exporting to PDF" : "Export to PDF";
    }

    get formioFormVisible(): boolean {
        return this.generatingPdf || !this.dataSubmitted && !this.dataFilledAlready;
    }

    @ViewChild("formioForm") formioFormComponent: FormioComponent;
    @ViewChild("formioFormParent") formioFormParent: ElementRef<HTMLDivElement>;

    dateToMySQLDate(date: Date | undefined): string {
        if (date == undefined) return "";
        return date.toISOString().slice(0, 19).replace("T", " ");
    }

    setEndOfDay = (date: Date): Date => {
        if (!date) return date;
        const endOfDay = new Date(date);
        endOfDay.setHours(23, 59, 59, 999);
        return endOfDay;
    };

    // Function to handle submission with custom error handling
    beforeSubmit = async (submission, callback) => {
        this.submission = submission;
        this.callback = callback;

            if (this.episodeMandatory && !this.episode /* && !this.isExternalUser */) {
            this.unsubmittedData = {submission, callback}
            callback({message: "Select episode before saving"}, null);
            this.selectEpisode();
            return;
        }

        if (this.revisionId && !this.revision) {
            if (!this.anonymousSelfRegForm) {
                this.revision = await this.formIORevisionService.get(this.revisionId);
            } else {
                const formDef = await this.selfRegistrationFormService.getFormDefinitionByOrgUrl(this.selfRegOrgUrl);
                this.revision = formDef.activeRevision;
            }
        }

        if (this.shareOnComplete
            && (!this.revision || !this.revision.isEditable)
            && (!this.isMultiUserForm || this.isLastStage)
        ) {
            this.unsubmittedData = {submission, callback};

            const result = await this.formIoShareDialogService.show({
                shareFileId: this.shareFile?.id_original,
                formInstanceId: this.formDataId
            })

            if (result.status === ShareFormDialogResultStatus.Canceled) {
                callback({message: "Form sharing canceled"}, null);
                this.dataSubmitted = false;
                return;
            }

            await this.onShare(result.data);
        }

        await this.submit();
    }

    async submit() {
        const submission = this.submission;
        const callback = this.callback;
        try {
            this.sendingData = true;
            // This is to avoid a draft being sent while the data is being submitted
            this.draftSendEnabled = false;
            clearTimeout(this.draftTimeoutId);


            if (this.requestId) {
                // Saving a Requested Form
                const formData = <FormIOData>{
                    id: this.formDataId,
                    createdOn: new Date(),
                    json: JSON.stringify({data: submission.data}),
                    formDefinitionId: this.formDefId,
                    formRevisionId: this.revisionId,
                    requestId: +this.requestId.split("-")[0],
                    // RevisionUsed: +this.re,
                    //  UserID: this.userService.currentUser.id,
                    draftId: this.formDraft?.id_original,
                    //  OrganizationID: this.userService.currentUser.companyId,
                    episodeId: this.episode.id,
                    patientId: this.patientId,
                    walkInId: this.walkInId
                }

                if (this.formioMiProExtraParameters) {
                    formData.medicalHistoryGridFromDate = SetUTCTimeZone(this.formioMiProExtraParameters.medicalHistoryGridFromDate);
                    formData.medicalHistoryGridToDate = SetUTCTimeZone(this.formioMiProExtraParameters.medicalHistoryGridToDate);
                    formData.consultationHistoryGridFromDate = SetUTCTimeZone(this.formioMiProExtraParameters.consultationHistoryGridFromDate);
                    formData.consultationHistoryGridToDate = SetUTCTimeZone(this.formioMiProExtraParameters.consultationHistoryGridToDate);
                    formData.episodeNotesGridEpisodeId = this.formioMiProExtraParameters.episodeNotesGridEpisodeId;
                    formData.episodeNotesGridFromDate = SetUTCTimeZone(this.formioMiProExtraParameters.episodeNotesGridFromDate);
                    formData.episodeNotesGridToDate = SetUTCTimeZone(this.formioMiProExtraParameters.episodeNotesGridToDate);
                    formData.selectedEpisodesJson = this.formioMiProExtraParameters.selectedEpisodesJson;
                }

                // Attempt to save form data
                let resultFormDataId;
                if (this.isDraft) {
                    const result = await this.formIODataService.update(this.formDataId, formData);
                    resultFormDataId = result.id;
                } else {
                    const result = await this.formIODataService.sendFormDataJson(formData);
                    this.dataSubmitted = result.isSuccess;
                    resultFormDataId = this.formDataId;
                }

                if (!this.dataSubmitted) {
                    callback({message: "Data submission failed.", component: null}, null);
                    return;
                }

                if (this.formDataId && (!this.isDraft || this.submitDraft)) {
                    let fileDetails = await this.createPDFReport(resultFormDataId, submission.data);
                    if (this.shareOnComplete && this.shareDto) {
                        this.shareDto.fileDetailsId = fileDetails.id_original as any;
                    }
                }

                await this.updateEpisodeDraftFormStatus();
            } else if (this.isMultiUserForm) {
                // saving a MultiUser Form

                const initialSubmission = JSON.parse(JSON.stringify(submission));

                if (this.formDataJsonOriginal.data) {
                    const keys = Object.keys(this.formDataJsonOriginal.data);
                    keys.forEach(key => {
                        // remove data that's already on the original formData, get only the changes
                        if ((submission.data[key].value) && JSON.stringify(submission.data[key].value) == JSON.stringify(this.formDataJsonOriginal.data[key])) {
                            delete submission.data[key];
                        } else if (JSON.stringify(submission.data[key]) == JSON.stringify(this.formDataJsonOriginal.data[key])) {
                            delete submission.data[key];
                        }
                    });
                }
                // Merging is done on the API side by sending only the values that changed
                // saving a MultiUser Form
                const formData = <FormIOData>{
                    id: this.formDataId,
                    createdOn: new Date(),
                    isMUF: true,
                    mufAssocId: this.mufAssocId,
                    multiUserFormAssocLinkId: this.currentStageId,
                    json: JSON.stringify({data: submission.data}),
                    formDefinitionId: this.formDefId,
                    formRevisionId: this.revisionId,
                    requestId: this.requestId,
                    // RevisionUsed: +this.re,
                    // UserID: this.userService.currentUser.id,
                    draftId: this.formDraft?.id_original,
                    // OrganizationID: this.userService.currentUser.companyId,
                    episodeId: this.episode ? this.episode.id : null,
                    patientId: this.patientId,
                    walkInId: this.walkInId
                }

                if (this.formioMiProExtraParameters) {
                    formData.medicalHistoryGridFromDate = SetUTCTimeZone(this.formioMiProExtraParameters.medicalHistoryGridFromDate);
                    formData.medicalHistoryGridToDate = SetUTCTimeZone(this.formioMiProExtraParameters.medicalHistoryGridToDate);
                    formData.consultationHistoryGridFromDate = SetUTCTimeZone(this.formioMiProExtraParameters.consultationHistoryGridFromDate);
                    formData.consultationHistoryGridToDate = SetUTCTimeZone(this.formioMiProExtraParameters.consultationHistoryGridToDate);
                    formData.episodeNotesGridEpisodeId = this.formioMiProExtraParameters.episodeNotesGridEpisodeId;
                    formData.episodeNotesGridFromDate = SetUTCTimeZone(this.formioMiProExtraParameters.episodeNotesGridFromDate);
                    formData.episodeNotesGridToDate = SetUTCTimeZone(this.formioMiProExtraParameters.episodeNotesGridToDate);
                    formData.selectedEpisodesJson = this.formioMiProExtraParameters.selectedEpisodesJson;
                }

                // Attempt to save form data
                let result: FormDataResponse;
                if (!this.anonymousSelfRegForm) {
                    result = await this.formIODataService.sendFormDataJson(formData);
                } else {
                    result = await this.selfRegistrationFormService.saveFormData(this.selfRegOrgUrl, this.patientId, formData);
                }
                this.dataSubmitted = result.isSuccess;

                if (this.dataSubmitted && result.allStagesComplete) {
                    let fileDetails = await this.createPDFReport(this.formDataId, initialSubmission.data);
                    if (this.shareOnComplete && this.shareDto) {
                        this.shareDto.fileDetailsId = fileDetails.id_original as any;
                    }
                    await this.updateEpisodeDraftFormStatus();
                }
                if (!this.dataSubmitted) {
                    callback({message: "Data submission failed.", component: null}, null);
                    return;
                }
            } else {

                // saving a 'Free' Form
                const formData = <FormIOData>{
                    createdOn: new Date(),
                    json: JSON.stringify({data: submission.data}),
                    isMUF: false,
                    surveyId: this.surveyId ?? null,
                    formDefinitionId: this.formDefId,
                    formRevisionId: this.revisionId,
                    // RevisionUsed: +this.re,
                    // UserID: this.userService.currentUser.id,
                    draftId: this.formDraft?.id_original,
                    // OrganizationID: this.userService.currentUser.companyId,
                    id: this.formDataId ?? null,
                    episodeId: this.episode.id,
                    patientId: this.patientId,
                    walkInId: this.walkInId,
                    isReadOnlyEpisode: this.isReadOnlyEpisode
                }

                if (this.formioMiProExtraParameters) {
                    formData.medicalHistoryGridFromDate = SetUTCTimeZone(this.formioMiProExtraParameters.medicalHistoryGridFromDate);
                    formData.medicalHistoryGridToDate = SetUTCTimeZone(this.formioMiProExtraParameters.medicalHistoryGridToDate);
                    formData.consultationHistoryGridFromDate = SetUTCTimeZone(this.formioMiProExtraParameters.consultationHistoryGridFromDate);
                    formData.consultationHistoryGridToDate = SetUTCTimeZone(this.formioMiProExtraParameters.consultationHistoryGridToDate);
                    formData.episodeNotesGridEpisodeId = this.formioMiProExtraParameters.episodeNotesGridEpisodeId;
                    formData.episodeNotesGridFromDate = SetUTCTimeZone(this.formioMiProExtraParameters.episodeNotesGridFromDate);
                    formData.episodeNotesGridToDate = SetUTCTimeZone(this.formioMiProExtraParameters.episodeNotesGridToDate);
                    formData.selectedEpisodesJson = this.formioMiProExtraParameters.selectedEpisodesJson;
                }

                // Attempt to save form data
                let resultFormDataId;
                if (this.isDraft) {
                    formData.isDraft = true;
                    const result = await this.formIODataService.update(this.formDataId, formData);
                    this.dataSubmitted = true;
                    resultFormDataId = result.id;
                } else {
                    let result: FormDataResponse;
                    if (!this.anonymousSelfRegForm) {
                        result = await this.formIODataService.sendFormDataJson(formData);
                    } else {
                        result = await this.selfRegistrationFormService.saveFormData(this.selfRegOrgUrl, this.patientId, formData);
                    }
                    this.dataSubmitted = result.isSuccess;
                    resultFormDataId = result.formDataId;
                }
                this.formDataId = resultFormDataId;

                if (!this.dataSubmitted) {
                    callback({message: "Data submission failed.", component: null}, null);
                    return;
                }

                if (this.formDataId && (!this.isDraft || this.submitDraft)) {
                    // Create Report
                    let fileDetails = await this.createPDFReport(resultFormDataId, submission.data);
                    if (this.shareOnComplete && this.shareDto) {
                        this.shareDto.fileDetailsId = fileDetails.id_original as any;
                    }
                }

                if (!this.anonymousSelfRegForm) {
                    await this.updateEpisodeDraftFormStatus();
                }
            }
            // Report Success
            callback(null, null);

            if (this.shareOnComplete && this.shareDto) {
                await this.fromIODataViewerService.share(this.formDataId, this.shareDto);
            }

            if (this.anonymousSelfRegForm) return;

            if (this.dataSubmitted) {
                this.messageService.add(Message.Success("Data submitted successfully."));
                if (!this.restoreFormDraft) {
                    if (this.noteDraftId) {
                        const returnURL: string = AppStorage.getItemFromSession("returnAddNoteURL").toString().split("&noteDraftId")[0];
                        AppStorage.setItemForSession("returnAddNoteURL", null);

                        const urlTree: UrlTree = this.router.parseUrl(returnURL);

                        urlTree.queryParams["noteDraftId"] = this.noteDraftId;
                        urlTree.queryParams["formDataId"] = this.formDataId;

                        return this.router.navigateByUrl(urlTree.toString()).then()
                    }
                }

                if (this.returnUrl) {
                    if (this.useBackNavigationParams && AppStorage.getItemFromSession("backNavigationParams")) {
                        let backNavigationParams = AppStorage.getItemFromSession("backNavigationParams")
                        AppStorage.setItemForSession("backNavigationParams", null);
                        return this.router.navigate([this.returnUrl], {queryParams: backNavigationParams});
                    }

                    this.router.navigateByUrl(this.returnUrl);
                    return;
                }

                const previousUrl = this.navigationHistoryService.getPreviousUrl();
                if (previousUrl) {
                    this.navigationHistoryService.removeCurrentUrl();
                    return this.router.navigate([previousUrl.url], { queryParams: previousUrl.queryParams });
                }

                if (this.goBackOnSubmit) {
                    this.location.back();
                }
            }

        } catch (error) {
            callback({message: error, component: null}, null);

        } finally {
            this.patientService.setPatientSelectionLock(false);

            // to remove animation from the Buttons
            this.formDataSaved?.emit(this.formDataId)
            this.refreshForm.emit({
                form: this.formJson
            });

            this.draftSendEnabled = true;
            this.sendingData = false;

            setTimeout(() => {
                callback(null, null);
            }, 250);
        }
    }

    constructor() {
        // Add the beforeSubmit function as a hook
        this.formOptions = {
            hooks: {
                beforeSubmit: this.beforeSubmit,
            }
        }
    }

    selectEpisode() {
        if (this.episode == null) {
            this.selectEpisodeBeforeSaving.emit();
        }
    }

    async onShare(shareDto: ShareFormDataDTO) {
        this.shareDto = {
            fileDetailsId: shareDto.fileDetailsId ?? this.shareDto?.fileDetailsId,
            emails: shareDto.emails ?? this.shareDto?.emails,
            userIds: shareDto.userIds ?? this.shareDto?.userIds
        };

        await this.saveData(this.episode);
    }

    public async saveData(episode: IEpisode) {
        this.episode = episode;
        if (this.unsubmittedData != null) {
            this.unsubmittedData = null;
            await this.discardDraft();
        }
    }

    ngOnInit(): void {
        this.clinicianFieldTypes = [
            "miproClinicianRegistrationNoTextField",
            "miproClinicianNameTextField",
            "miproClinicianOrgTextField",
            "miproClinicianEmailField",
            "miproClinicianPhoneField"
        ];

        this.autoPopulatedFieldTypes = [
            "select", "textfield",
            "textarea", "datagrid",
            "signature", "panel", "day",
            "miproClinicianRegistrationNoTextField",
            "miproClinicianNameTextField",
            "miproClinicianOrgTextField",
            "miproClinicianPhoneField",
            "miproClinicianEmailField",
            "miproPatientAddressTextField",
            "miproPatientNameTextField",
            "miproPatientDateOfBirth",
            "miproPatientEmailField",
            "miproPatientPhoneField",
            "miproPatientAgeNumber",
            "miproSignatureField",
        ];
    }

    async ngAfterViewInit(): Promise<void> {
        try {
            this.prism?.init();
        } catch {
        }

        this.formLoading = true;

        this.isExternalUser = this.userHelperService.hasRole(FormioRole.FormioExternalUser);

        // Multi-User forms and Requested Forms must have a formData instance
        if ((this.isMultiUserForm || this.requestId) && !this.formDataId) {
            this.invalidParameters = true;
            return;
        }

        this.showCancelButton = !this.readOnlyForm && this.isDraft;
        // Сonvert the date to UTC format using SetUTCTimeZone and then format it to MySQL date string
        const formioMiProParametersString = [
            `${this.dateToMySQLDate(SetUTCTimeZone(this.formioMiProExtraParameters.medicalHistoryGridFromDate)) ?? ""}`,
            `${this.dateToMySQLDate(SetUTCTimeZone(this.setEndOfDay(this.formioMiProExtraParameters.medicalHistoryGridToDate))) ?? ""}`,
            `${this.dateToMySQLDate(SetUTCTimeZone(this.formioMiProExtraParameters.consultationHistoryGridFromDate)) ?? ""}`,
            `${this.dateToMySQLDate(SetUTCTimeZone(this.setEndOfDay(this.formioMiProExtraParameters.consultationHistoryGridToDate))) ?? ""}`,
            `${this.formioMiProExtraParameters.episodeNotesGridEpisodeId ?? ""}`,
            `${this.dateToMySQLDate(SetUTCTimeZone(this.formioMiProExtraParameters.episodeNotesGridFromDate)) ?? ""}`,
            `${this.dateToMySQLDate(SetUTCTimeZone(this.setEndOfDay(this.formioMiProExtraParameters.episodeNotesGridToDate))) ?? ""}`,
        ];

        const userId = this.isMultiUserForm && this.mufUserId ? this.mufUserId : this.userService.currentUser.id;
        let organizationId = this.isMultiUserForm && this.mufOrgId ? this.mufOrgId.toString() : this.userHelperService.organizationId;

        if(this.userHelperService.isPassportOrganization){
            organizationId = "0";
        }

        const formDefinitionParameters = <FormDefinitionParameters>{
            userId: userId,
            organizationId: organizationId,
            patientId: this.patientId ?? "",
            medicalHistoryGridFromDate: this.dateToMySQLDate(SetUTCTimeZone(this.formioMiProExtraParameters.medicalHistoryGridFromDate)) ?? "",
            medicalHistoryGridToDate: this.dateToMySQLDate(SetUTCTimeZone(this.setEndOfDay(this.formioMiProExtraParameters.medicalHistoryGridToDate))) ?? "",
            consultationHistoryGridFromDate: this.dateToMySQLDate(SetUTCTimeZone(this.formioMiProExtraParameters.consultationHistoryGridFromDate)) ?? "",
            consultationHistoryGridToDate: this.dateToMySQLDate(SetUTCTimeZone(this.setEndOfDay(this.formioMiProExtraParameters.consultationHistoryGridToDate))) ?? "",
            episodeNotesGridEpisodeId: this.formioMiProExtraParameters.episodeNotesGridEpisodeId ?? "",
            episodeNotesGridFromDate: this.dateToMySQLDate(SetUTCTimeZone(this.formioMiProExtraParameters.episodeNotesGridFromDate)) ?? "",
            episodeNotesGridToDate: this.dateToMySQLDate(SetUTCTimeZone(this.setEndOfDay(this.formioMiProExtraParameters.episodeNotesGridToDate))) ?? "",
            parameterString: [`${userId}`, `${organizationId}`, `${this.patientId ?? ""}`, ...formioMiProParametersString, ...(this.extraData ? this.extraData.split("彁") : [])]
        };

        // Fetch form definition
        let result: FormIODefinition;

        if (!this.anonymousSelfRegForm) {
            result = await this.formIODefinitionService.getFormJson(this.formDefId, this.revisionId, this.readOnlyForm, formDefinitionParameters);
            result.json = await this.formIORevisionService.downloadContentFromStorage(result.json);
        } else {
            result = await this.selfRegistrationFormService.getFormDefinitionJsonByOrgUrl(this.selfRegOrgUrl);
        }

        //Custom values for signature component
        result.json = this.updateSignatureFields(result.json);

        this.formDefinitionName = result.name;
        // The resultant form definition DTO contains a JSON and MobileFriendly properties which depends on the revisionId passed on, this will allow to render any Form Revision, not just the Active One
        // If no, then the id is passed then the Active one will be used
        this.mobileFriendly = !!result.mobileFriendly;

        let parsedJson = JSON.parse(result.json);
        if (!this.formDataId && !this.anonymousSelfRegForm) {
            parsedJson = await this.populateCustomDataGrids(parsedJson);
        }
        this.formJson = parsedJson;

        // Detect Image uploaders
        this.detectImageUploaders(this.formJson);
        // Injecting select values into the JSON Object (execute only if in a non-readonly mode)
        if (this.requestId || this.isMultiUserForm || (result.fields?.some(x => x) && !this.readOnlyForm)) {

            // Find and substitute values(on "fields") for each component with specific keys.
            this.prepareStateTabs(this.formJson, this.mufStages);
            this.populateCustomFields(this.formJson, result.fields);

            // Refresh formio with the updated form's components list
            this.refreshForm.emit({form: this.formJson});
        }
        if (this.readOnlyForm) {
            // Remove buttons from the form definition
            let components = this.formJson.components as ExtendedComponentSchema[];
            components = components.filter(component => component.type !== "button");
            this.formJson.components = components;
            // --------------
        }
        // If there is a dataId then fetch the associated FormData
        if (this.formDataId) {
            let resultData: string;
            if (!this.anonymousSelfRegForm) {
                resultData = await this.formIODataService.getFormDataJson(this.formDataId);
            } else {
                resultData = await this.selfRegistrationFormService.getFormDataJsonByUrl(this.selfRegOrgUrl, this.patientId);
            }

            if (resultData) {
                let parsedResultData;

                if (typeof resultData === "string") {
                    parsedResultData = JSON.parse(resultData);
                } else {
                    parsedResultData = resultData;
                }

                // ResultData contains the FormData json value, if this is more than '{}' means it has a 'proper' value so it can't be "modified"
                if (this.requestId && resultData.length > 3) {
                    // This form has already been filled
                    this.dataFilledAlready = true;
                }

                if (this.isMultiUserForm) {
                    this.formDataJsonOriginal = parsedResultData;
                }

                this.formDataJson = this.flattenSubmission(parsedResultData);

                // Save the initial state of the form data if this form is a multi-user form
                // This is needed to check what to change and not during Form Saving to avoid overwriting other users' data

                // Check if formData has file_attachments
                if (this.formDataJson.data) {
                    this.hasFileAttachments = Object.keys(this.formDataJson.data).includes("file_attachments");
                }
                // This is a fix when using images with base64, we need to check if this is still being used, if not, it can be removed/commented out
                for (const key in this.formDataJson.data) {
                    this.formDataJson.data[key] != null
                    && this.formDataJson.data[key] != false
                    && Array.from(this.formDataJson.data[key]).forEach(element => {
                        // If it has an url and originalName fields, it means it is a file
                        if (element["url"] && element["originalName"]) {
                            let url = element["url"].toString();
                            if (!url.startsWith("http")) {
                                url = "https://localhost:5000/" + url;
                            }
                            if (key !== "file_attachments") {
                                const data = this.readBase64TextFromFileOnURL(url);
                                element["url"] = data;
                            }
                        }
                    });
                }
                if (this.isMultiUserForm && !this.anonymousSelfRegForm) {
                    // Check for any draft saved and 'apply it' accordingly
                    this.formDraftFromServer = await this.formIODataDraftService.getFormDataDraftJson(this.formDefId, this.userService.currentUser.id, this.patientId);
                    this.draftPromptPending = this.formDraftFromServer != null;
                    this.showExistingDraftMessage.emit(this.formDraftFromServer != null);
                    if (this.restoreFormDraft && this.formDraftFromServer != null) {
                        this.applyDraft();
                    }
                }

                if (!this.anonymousSelfRegForm) {
                    this.formJson = await this.populateCustomDataGrids(this.formJson);
                }
            }
        } else if (!this.anonymousSelfRegForm) {
            // Check for any draft saved and 'apply it' accordingly
            this.formDraftFromServer = await this.formIODataDraftService.getFormDataDraftJson(this.formDefId, this.userService.currentUser.id, this.patientId);
            this.draftPromptPending = this.formDraftFromServer != null;
            this.showExistingDraftMessage.emit(this.formDraftFromServer != null);
            if (this.restoreFormDraft && this.formDraftFromServer != null) {
                this.applyDraft();
            }
        }

        this.refreshForm.emit({
            form: this.formJson
        });

        this.formLoading = false;

        if (this.isMultiUserForm) await this.updateClinicianData();

        if (this.formDataId && this.generatePdf) {
            setTimeout(async () => {
                const resultData = await this.formIODataService.getFormDataJson(this.formDataId);
                if (resultData) {
                    let fileDetails = await this.createPDFReport(this.formDataId, JSON.parse(resultData).data);
                    this.initialPdfGenerated.emit();

                    if (this.shareOnComplete) {
                        this.shareDto = {
                            fileDetailsId: fileDetails.id_original as any
                        };
                        this.displayShareFileDialog = true;
                    }
                }
            }, 1000);

        }
    }


    // Recursive method to explore and fix each state-tab edition access accordingly.
    private prepareStateTabs(component: ExtendedComponentSchema, stages: MUFStage[]): void {
        if (!component) return;

        // Recursively fix descendant tabs.
        this.getInnerComponents(component).forEach(child => this.prepareStateTabs(child, stages));

        // Ignore this field if it is not a state-tabs component.
        if (component.type !== "state-tabs") return;

        component.components?.forEach((tab: ExtendedComponentSchema) => {
            // If this is a multi-user form, un-authorize edition for other tabs different from the one of the current stage.
            if (this.isMultiUserForm) {
                // Get only muf stages corresponding to this tab.
                // Sort the stages by action value, in descending order: Hide(0) < ReadOnly(1) < Edit(2).
                const tabStages: MUFStage[] = stages
                    .filter(stage => stage.tabKey == tab.key)
                    .sort((first, second) => second.action - first.action);

                // Get the highest action value: Hide(0) < ReadOnly(1) < Edit(2).
                tab.state = tabStages.length > 0 ? tabStages[0].action as TabState : TabState.Hide;

                // If this component is not hide, continue.
                if (tab.state > 0) return;

                // Otherwise, clean custom validators of its inner components.
                tab.components?.forEach((child: ExtendedComponentSchema) => {
                    if (!!child?.validate) child.validate.custom = "";
                });

                // If this is not a multiuser form, then authorize edition.
            } else tab.state = 2;
        });
    }

    // Recursive method to explore and expand each component to inject values on its data field accordingly.
    private populateCustomFields(component: ExtendedComponentSchema, fields: FormFields[]): void {
        if (!component) return;

        // Recursively populate descendant fields (omitting un-accessible state-tabs).
        this.getInnerComponents(component)
            .filter(child => component.type !== "state-tabs" || child.state > 1)
            .forEach(child => this.populateCustomFields(child, fields));

        // Ignore this field if it is not a self-populated one.
        if (!component.key?.startsWith("dbo.") || !this.autoPopulatedFieldTypes.includes(component.type)) return;

        // If the field is a datagrid (custom or not).
        if (component.type == "datagrid" || component.type.startsWith("mipro") && component.type.endsWith("Datagrid")) {
            component.defaultValue = this.getComponentFormField(component, fields)?.values
                .map(values => JSON.parse(values.value.toString() ?? ""));
            return;
        }

        // If the field is a panel component.
        if (component.type == "panel") {
            const data = this.getComponentFormField(component, fields)?.values[0];

            switch (data?.value) {
                case "1":
                case "true":
                case "True":
                    component.hidden = false; // Show the panel.
                    break;
                case "0":
                case "false":
                case "False":
                    component.hidden = true; // Hide the panel.
                    break;
                default:
                    component.disabled = true; // Disable inner components to the panel (read-only).
                    break;
            }

            return;
        }

        // If the field is a signature component.
        if (component.type == "signature") {
            const data = this.getComponentFormField(component, fields)?.values[0];
            component.defaultValue = JSON.parse(data.value.toString()) ?? "";
            return;
        }

        // If this is a clinician field.
        if (this.clinicianFieldTypes.includes(component.type)) {
            // If the form is a single-user draft.
            if (!this.isMultiUserForm && this.isDraft) {
                // Disable the form component and show the "update clinician data" button.
                this.showUpdateClinicianDataButton = true;
                component.disabled = true;
            }
            // Register the clinician field key to be able to update clinician data later.
            this.clinicianFieldComponents.push(component);
        }

        // If the component has a "data" field (e.g, "select" elements).
        if (!!component.data) {
            // "Select" elements has a data property
            component.data.values = this.getComponentFormField(component, fields)?.values;
            component.data.values.forEach((value: FormFieldValues) => value.value = value.queryableValue);
            return;
        }

        // In any other case, just update the default value with the form field one.
        const data = this.getComponentFormField(component, fields)?.values[0];
        component.defaultValue = data?.value ?? "";
    }

    // Auxiliary method to get the form field corresponding to a given component.
    private getComponentFormField(component: ExtendedComponentSchema, fields: FormFields[]): FormFields {
        if (!component || !fields) return null;
        return fields.find(fields => fields.multiValue == false && fields.fieldKey == component.key.replace(/\d+$/, ""));
    }

    // Auxiliary method to get the inner components of a given component.
    private getInnerComponents(component: ExtendedComponentSchema): ExtendedComponentSchema[] {
        if (!component) return [];

        switch (component.type) {
            case "table":
                return this.getComponents(component.rows?.flat());
            case "columns":
                return this.getComponents(component.columns);
            default:
                return component.components ?? [];
        }
    }

    // Auxiliary method to get the inner components of each element in a given list of components.
    private getComponents(components: ExtendedComponentSchema[]): ExtendedComponentSchema[] {
        if (!components?.length) return [];
        return components.flatMap((column: ExtendedComponentSchema): ExtendedComponentSchema[] => column.components ?? []) ?? [];
    }


    // Updates the form with the clinician data associated with the current user.
    protected async updateClinicianData(): Promise<void> {
        if (!this.clinicianFieldComponents?.length || !this.formDataJson?.data) return;

        //TODO: See if it is possible to obtain the current organisation name in a better way.
        const currentUser = this.userService.currentUser as UserLogin;
        const userLogins = await this.linkUserService.getUserLogins();
        const currentUserLogin = userLogins.find(ul => ul.id === this.userService.currentUser.id);
        const currentOrganisation = currentUserLogin?.organizations
            .find(org => org.isCurrentTenant) ?? currentUserLogin?.organizations[0];

        const clinicianData = {
            "miproClinicianNameTextField": currentUser?.fullName ?? "",
            "miproClinicianOrgTextField": currentOrganisation?.name ?? "",
            "miproClinicianEmailField": currentUser?.email ?? "",
            "miproClinicianPhoneField": currentUser?.phoneNumber ?? "",
            "miproClinicianRegistrationNoTextField": currentUser?.registrationNumber ?? ""
        };

        this.updateFormJsonUserData(this.formDataJson.data, clinicianData);

        this.refreshForm.emit({form: this.formJson});
    }

    // TODO: Find a way to avoid the direct modification of the formDataJson object (it is very cumbersome and error-prone),
    //  by modifying the current value of the component directly.
    //  Modifying the formJsonData will also become unnecessary if the "update clinician data" button feature is removed.
    private updateFormJsonUserData(formDataJson: any, currentUserData: any, path: string = ""): void {
        if (!formDataJson || !currentUserData) return;

        for (const key in formDataJson) {
            // Update the current path.
            const nestedPath: string = `${path}.${key}`;

            // Find a clinician field component that match with this path.
            const component = this.clinicianFieldComponents
                .find(component => nestedPath.endsWith(component.key));

            // If the component is found, update the form data with the corresponding clinician data.
            if (!!component && currentUserData[component.type] != null) {
                formDataJson[key] = currentUserData[component.type];

                // Otherwise, call recursively if the element at the current path is an object.
            } else if (formDataJson[key] != null && typeof formDataJson[key] === "object") {
                this.updateFormJsonUserData(formDataJson[key], currentUserData, nestedPath);
            }
        }
    }

    private async populateCustomDataGrids(json: any) {
        if (this.isMultiUserForm || this.mufAssoc) return await this.populateCustomMufDataGrids(json);

        let componentTypes = await this.formIODefinitionService.getCustomComponentTypes(this.formDefId)

        if ((componentTypes & CustomComponentTypes.MedicationHistory) == CustomComponentTypes.MedicationHistory) {
            json = await this.updateMedHistDataGridFields(json, this.formioMiProExtraParameters);
        }
        if ((componentTypes & CustomComponentTypes.ConsultationHistory) == CustomComponentTypes.ConsultationHistory) {
            json = await this.updateConsultHistDataGridFields(json, this.formioMiProExtraParameters);
        }
        if ((componentTypes & CustomComponentTypes.EpisodeNotes) == CustomComponentTypes.EpisodeNotes) {
            json = await this.updateEpisodeNotesDataGridFields(json, this.formioMiProExtraParameters);
        }
        if ((componentTypes & CustomComponentTypes.ConsultationHistoryV2) == CustomComponentTypes.ConsultationHistoryV2) {
            json = await this.updateConsultHistDataGridV2Fields(json, this.formioMiProExtraParameters);
        }

        return json;
    }

    private async populateCustomMufDataGrids(json: any) {
        // If there is no current stage id, then it is necessary to load MUF assoc again because the loaded one was loaded without the stages.
        if (!this.currentStageId || !this.mufAssoc) {
            this.mufAssoc =
                await this.multiUserFormAssociationsService.getMufAssociationsByFormDataId(this.formDataId) ??
                await this.multiUserFormAssociationsService.get(this.mufAssocId);
        }

        let stages = this.currentStageId
            ? [this.mufAssoc.multiUserFormAssociationLinks.find(x => x.id == this.currentStageId)]
            : [...this.mufAssoc.multiUserFormAssociationLinks];

        for (const stage of stages) {
            let componentTypes = await this.multiUserFormAssociationsService.getStageCustomComponentTypes(stage.id)
            json = await this.populateCustomMufDataGridsForStage(json, stage, componentTypes);
        }

        return json;
    }

    private async populateCustomMufDataGridsForStage(json: any, stage: MultiUserFormAssociationLink, componentTypes: number) {
        const extraParams: FormioMiProExtraParameters = {
            selectedEpisodesJson: stage.selectedEpisodesJson,
            episodeNotesGridFromDate: stage.episodeNotesGridFromDate,
            episodeNotesGridToDate: stage.episodeNotesGridToDate,
            consultationHistoryGridFromDate: stage.consultationHistoryGridFromDate,
            consultationHistoryGridToDate: stage.consultationHistoryGridToDate,
            medicalHistoryGridFromDate: stage.medicalHistoryGridFromDate,
            medicalHistoryGridToDate: stage.medicalHistoryGridToDate,
            episodeNotesGridEpisodeId: stage.episodeNotesGridEpisodeId || undefined,
        }

        if ((componentTypes & CustomComponentTypes.MedicationHistory) == CustomComponentTypes.MedicationHistory) {
            json = await this.updateMedHistDataGridFields(json, extraParams, stage);
        }
        if ((componentTypes & CustomComponentTypes.ConsultationHistory) == CustomComponentTypes.ConsultationHistory) {
            json = await this.updateConsultHistDataGridFields(json, extraParams, stage);
        }
        if ((componentTypes & CustomComponentTypes.EpisodeNotes) == CustomComponentTypes.EpisodeNotes) {
            json = await this.updateEpisodeNotesDataGridFields(json, extraParams, stage);
        }
        if ((componentTypes & CustomComponentTypes.ConsultationHistoryV2) == CustomComponentTypes.ConsultationHistoryV2) {
            json = await this.updateConsultHistDataGridV2Fields(json, extraParams, stage);
        }

        return json;
    }

    // Detect all imageUploaders in the form
    detectImageUploaders(component: ExtendedComponentSchema) {
        const children = component["components"];
        if (children) {
            children.forEach(element => {
                // Call recursively to process inner components
                this.detectImageUploaders(element);
            });
            // Select those of type imageuploader and get the Key ID
            const chosen = children.filter(v => (v.type === "imageuploader"));
            chosen.forEach(element => {
                this.imagesKeysPerImageUploaderComponent[element.key] = "";
            });
        }
    }


    async createPDFReport(formId: EntityId, jsonData: any): Promise<FileDetails> {
        this.generatingPdf = true;
        try {
            if (this.revisionId) {
                let revision: FormRevisionDTO;
                if (!this.anonymousSelfRegForm) {
                    revision = await this.formIORevisionService.get(this.revisionId);
                } else {
                    const formDef = await this.selfRegistrationFormService.getFormDefinitionByOrgUrl(this.selfRegOrgUrl);
                    revision = formDef.activeRevision;
                }
                if (revision.isEditable && this.isDraft) return;
            }

            // Force full redraw
            this.refreshForm.emit({ submission: this.formioFormComponent.submission });
            await firstValueFrom(this.formioFormComponent.change);

            const formElement: HTMLDivElement = this.formioFormParent.nativeElement;
            const parsedHtml: Document = this.formioHtmlParserService.parseFromElement(formElement, jsonData);
            let htmlContent: string = parsedHtml.body.innerHTML;

            const formioHTMLData = <FormioHTMLData>{
                htmlContent: htmlContent,
                formData: jsonData
            }
            const customResponseHandlerSettings: IHttpResponseHandlerSettings = {
                showSuccessMessage: true,
                successMessage: "Form Report Created Successfully"
            };
            let result: FileDetails;
            if (!this.anonymousSelfRegForm) {
                result = await this.formIODataService.createPdfFormData(formId, formioHTMLData, customResponseHandlerSettings);
            } else {
                result = await this.selfRegistrationFormService.createPdfFormData(this.selfRegOrgUrl, this.patientId, formioHTMLData, customResponseHandlerSettings);
            }

            // Window.open(res.url, "_blank");
            return result;
        } catch {
            this.messageService.add(Message.Error("PDF report creation failed"));

        } finally {
            this.generatingPdf = false;
        }
    }

    private async updateEpisodeDraftFormStatus() {
        if (this.episode.state == EpisodeStatus.FormDraft) {
            this.episode.state = EpisodeStatus.Active;
        }
        await this.episodeService.update(this.episode.id, this.episode);
    }

    private flattenSubmission(submission) {
        const simplifiedData = {};

        const traverseAndSimplify = (obj) => {
            const simplifiedObj = {};
            for (let key in obj) {
                if (Array.isArray(obj[key])) {
                    simplifiedObj[key] = obj[key];
                } else if (typeof obj[key] === "object" && obj[key] !== null) {
                    if (obj[key].hasOwnProperty("value")) {
                        simplifiedObj[key] = obj[key].value;
                    } else {
                        simplifiedObj[key] = traverseAndSimplify(obj[key]);
                    }
                } else {
                    simplifiedObj[key] = obj[key];
                }
            }
            return simplifiedObj;
        };

        simplifiedData["data"] = traverseAndSimplify(submission.data);
        return simplifiedData;
    };

    // To generate and print the form into a PDF
    async generatePDF(libraryToUse: PDFLibrary.jsPDF | PDFLibrary.jsReport = PDFLibrary.jsReport) {
        this.generatingPdf = true;

        const htmlFromDivParentOfFormioTag = this.formioFormParent.nativeElement.outerHTML;

        const jsReport: IReportGenerator = new FormioPDFGenerator(<IReportResource>{
            html: htmlFromDivParentOfFormioTag,
            jsonData: this.formDataJson.data,
            library: libraryToUse,
            formDefinitionName: this.formDefinitionName.replace(/\./g, "_")
        });

        await jsReport.generateReport();

        if (this.hasFileAttachments) {
            this.downloadAttachments();
            this.messageService.add(Message.Success("Check your downloads folder for the PDF and attachments.", "PDF downloaded."));
        }

        this.generatingPdf = false;
    }

    downloadAttachments = async () => {
        const zipArchiveBlob = await this.formIOFileUploaderService.getFileAttachments(this.formDataId);

        this.downloadZIP(zipArchiveBlob);
    }

    private downloadZIP(blob: Blob): void {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.target = "_blank";
        // Set the archive name
        link.download = `${this.formDefinitionName}_attachments`;
        document.body.appendChild(link);
        link.click();
        link.remove();
    }

    applyDraft() {
        this.draftPromptPending = false;
        this.showExistingDraftMessage.emit(false);
        this.formDraft = this.formDraftFromServer;
        // This is for an easier identification that his draft is already on server
        this.formDraft.id_original = this.formDraftFromServer.id_original;
        // this.formDraft.formRevisionId = this.revisionId;
        this.formDataJson = JSON.parse(this.formDraft.json);

        if (this.formDraftFromServer.episode) {
            this.episode = this.formDraftFromServer.episode;
        }

        if (this.episode && !this.restoreFormDraft) {
            this.episodeChanged.emit(this.episode.id);
        }

        // Update Images urls from S3 using fileStorage service to renew the url
        const componentKey = Object.keys(this.imagesKeysPerImageUploaderComponent) as string[];
        componentKey.forEach(async key => {
            if (key && this.formDataJson["data"][key]) {
                const imageKey = this.formDataJson["data"][key].key;
                const image = await this.formIOFileUploaderService.getImage(imageKey);
                this.imagesKeysPerImageUploaderComponent[image.key] = image?.url;
                this.formDataJson.data[key] =
                    {
                        key: image.key,
                        fileName: image.fileName,
                        url: image.thumbnailUrl,
                        uploadTime: new Date()
                    };
            }
        });
        this.refreshForm.emit({
            form: this.formJson
        });
    }

    // read text from URL location (the base64 encoded string)
    readBase64TextFromFileOnURL = (url: string) => {
        const request = new XMLHttpRequest();
        request.open("GET", url, false);
        request.send(null);
        return request.responseText;
    }

    applyImageUploaded(file: FileDetails) {
        this.showImageUploadDialog = false;
        // Initialize Form Data if it is not already created correctly
        if (!this.formDataJson?.data) {
            this.formDataJson = {
                data: {}
            }
        }
        // delete the previously uploaded image for the current imageloader
        this.formIOFileUploaderService.deleteFile(this.imagesKeysPerImageUploaderComponent[this.imageUploadKey]);

        this.formDataJson.data[this.imageUploadKey] = {
            key: file.key,
            fileName: file.fileName,
            url: file.thumbnailUrl,
            uploadTime: new Date()
        };
        this.imagesKeysPerImageUploaderComponent[this.imageUploadKey] = file.key;
        this.imageUploadKey = null;
        // manually trigger processChanges to update draft as this way to update the form submission data will not trigger an '($event.isModified && $event.isValid)' as isModified would be undefined (change is not coming from within the component)
        this.processChanges(this.formDataJson.data);
        // Force refresh to update the ImageUploader components
        this.refreshForm.emit({
            form: this.formJson
        });
    }

    discardImageUploaded(removeUploadedImg: boolean) {
        if (removeUploadedImg) {
            this.formIOFileUploaderService.deleteFile(this.imagesKeysPerImageUploaderComponent[this.imageUploadKey]);
        }
        this.showImageUploadDialog = false;
        this.imageUploadKey = null;
    }

    onChange($event) {
        // Ignore everything if the submit btn was clicked
        if (this.sendingData) {
            return;
        }
        // Ignore the event if it was triggered by submission (meaning it was not triggered by user input)
        if ($event.flags?.fromSubmission) {
            return;
        }
        if (this.readOnlyForm) {
            return;
        }


        // the click event triggers the 'onChange' without changing the value of the imageuploader component to avoid showing the dialog when applying a draft
        if ($event.isValid && !this.imageUploadKey && !$event.isModified
            && $event.changed?.component?.type === "imageuploader") {
            this.imageUploadKey = $event.changed?.component?.key;
            this.showImageUploadDialog = true;
        }
        if ($event.isModified || $event.changed) {
            this.formDataJson.data = $event.data;
            this.processChanges($event.data);
        }
    }

    // Process changes detected on the form Data, create Draft and/or sent the draft to API
    processChanges(data) {
        if (this.formDraft) {
            this.formDraft.json = JSON.stringify({data: data});
            this.formDraft.episodeId = this.episode?.id;
        } else {
            this.formDraft = <FormIODataDraft>{
                createdOn: new Date(),
                json: JSON.stringify({data: data}),
                formDefinitionId: this.formDefId,
                userId: this.userService.currentUser.id,
                patientId: this.patientId,
                episodeId: this.episode?.id,
                formDataId: this.formDataId
            }
        }
        this.scheduleDraftSend();
    }

    private scheduleDraftSend() {
        if (this.draftSendEnabled && !this.anonymousSelfRegForm && !this.draftPromptPending) {
            this.draftSendEnabled = false;
            this.draftTimeoutId = setTimeout(async () => {
                let sentJson: string = null;
                let sentEpisodeId: string;
                try {
                    if (!this.draftSendEnabled && this.formDraft) {
                        sentJson = this.formDraft.json;
                        sentEpisodeId = this.formDraft.episodeId;
                        const saved = await this.formIODataDraftService.sendFormDataDraftJson(this.formDraft);
                        if (saved && this.formDraft) {
                            this.formDraft.id = saved.id;
                            this.formDraft.id_original = saved.id_original;
                        }
                    }
                } finally {
                    this.draftSendEnabled = true;
                    if (this.formDraft && sentJson != null
                        && (this.formDraft.json !== sentJson || this.formDraft.episodeId !== sentEpisodeId)) {
                        this.scheduleDraftSend();
                    }
                }
            }, 3000);
        }
    }

    async discardDraft() {
        this.draftPromptPending = false;
        this.showExistingDraftMessage.emit(false);
        if (this.formDraftFromServer) {
            // delete all images detected on draft (images uploaded with the ImageUploaderComponent)
            const componentKey = Object.keys(this.imagesKeysPerImageUploaderComponent) as string[];
            const draft = JSON.parse(this.formDraftFromServer.json);
            componentKey.forEach(key => {
                if (key && draft["data"][key]) {
                    const imageKey = JSON.parse(draft["data"][key]).key;
                    this.formIOFileUploaderService.deleteFile(imageKey);
                }
            });
            // remove the draft itself
            await this.formIODataDraftService.removeFormDataDraft(this.formDraftFromServer.id_original);
        }
        this.formDraft = null;
        this.formDraftFromServer = null;
    }

    imageUploading(event: BbImageUploadEvent) {
        this.progress = 0;

        const formData = new FormData();
        formData.append(event.file.name, event.file);
        formData.append("last_modified", moment(event.file.lastModified).toDate().toUTCString());
        formData.append("max_size", this.maxSizePx.toString());
        formData.append("thumbnail_size", "400");
        formData.append("degree", event.degree.toString());
        formData.append("scaleX", event.scaleX.toString());
        formData.append("scaleY", event.scaleY.toString());

        this.formIOFileUploaderService.uploadFileWithProgress(formData).subscribe({
            next: e => {
                switch (e.type) {
                    case HttpEventType.UploadProgress:
                        this.progress = Math.round((100 * e.loaded) / e.total);
                        break;
                    case HttpEventType.Response:
                        this.messageService.add(Message.Success("Image successfully uploaded"));

                        const res = e.body as FilesUploadingResult;
                        if (res.uploadingStatus == FilesUploadingStatus.Success) {
                            const file = res.successfullyUploadedFiles[0];
                            if (file) {
                                file.lastUpdated = moment(file.lastUpdated).toDate();
                                file.uploadTime = moment(file.uploadTime).toDate();
                                this.applyImageUploaded(file);
                            } else {
                                this.discardImageUploaded(false);
                            }
                            /*
                                                        this.galleryImages.unshift(ImageUploaderComponent.mapImage(file));
                                                        this.imageUploader.clear();
                            */
                        }

                        //                        this.showImagesViewer = false;
                        //setTimeout(() => this.showImagesViewer = true, 100);
                        break;
                    case HttpEventType.ResponseHeader:
                        if (!e.ok) {
                            this.messageService.add(Message.Error(`${e.status}: ${e.statusText}`, "File Uploading"));
                        }
                        break;
                }
            },
            error: errorMsg => this.messageService.add(Message.Error(errorMsg, "File Uploading"))
        });
    }

    private updateSignatureFields(jsonString: string): string {
        // Parse the JSON string into an object
        const jsonData = JSON.parse(jsonString);

        function findAndUpdate(obj: any): void {
            // Check if the current object has a type property with the value "signature"
            if (obj.type === "signature") {
                // Set the sibling properties
                obj.minWidth = 0.5;
                obj.maxWidth = 0.5;
            }

            // Recursively check for nested objects or arrays
            for (const key in obj) {
                if (obj[key] && typeof obj[key] === "object") {
                    findAndUpdate(obj[key]);
                }
            }
        }

        findAndUpdate(jsonData);

        // Convert the updated object back to a JSON string
        return JSON.stringify(jsonData, null, 2);
    }

    private async updateMedHistDataGridFields(json: any, formioMiProExtraParameters: any, stage: MultiUserFormAssociationLink = null): Promise<string> {
        const organizationId = this.isMultiUserForm && this.mufOrgId ? this.mufOrgId : this.userHelperService.organizationId;

        const getDataForMedHistGrid = async (): Promise<any> => {
            let data: any[] = await this.formIODefinitionService.getMedHistDataGridValues(<any>{
                ...formioMiProExtraParameters,
                patientId: this.patientId,
                organizationId: organizationId
            });
            return data.map(x => JSON.parse(x));
        }

        let values: any[] = await getDataForMedHistGrid();

        const componentName = "miproMedHistDatagrid";
        const componentKey = !!stage ? this.getCustomGridKeyForStageTab(json, stage, componentName) : this.getCustomGridKey(json, componentName);
        const filedName = componentKey.split(".").pop();

        await this.findComponentAndUpdateDefaultValue(
            json,
            componentKey,
            async () => values
        );

        this.replaceInFormDataJson(
            filedName,
            values
        )

        return json;
    }

    private async updateConsultHistDataGridFields(json: any, formioMiProExtraParameters: any, stage: MultiUserFormAssociationLink = null): Promise<string> {
        const organizationId = this.isMultiUserForm && this.mufOrgId ? this.mufOrgId : this.userHelperService.organizationId;

        const getDataForConsultHistGrid = async (): Promise<any> => {
            let data: any[] = await this.formIODefinitionService.getConsultHistDataGridValues(<any>{
                ...formioMiProExtraParameters,
                patientId: this.patientId,
                organizationId: organizationId
            });
            return data.map(x => JSON.parse(x));
        }

        const componentName = "miproConsultHistDatagrid";
        const componentKey = !!stage ? this.getCustomGridKeyForStageTab(json, stage, componentName) : this.getCustomGridKey(json, componentName);
        const filedName = componentKey.split(".").pop();

        let values: any[] = await getDataForConsultHistGrid();
        await this.findComponentAndUpdateDefaultValue(
            json,
            componentKey,
            async () => values
        );

        this.replaceInFormDataJson(
            filedName,
            values
        )

        return json;
    }

    private async updateEpisodeNotesDataGridFields(json: any, formioMiProExtraParameters: any, stage: MultiUserFormAssociationLink = null): Promise<string> {
        const organizationId = this.isMultiUserForm && this.mufOrgId ? this.mufOrgId : this.userHelperService.organizationId;

        const getDataForEpisodeNotesGrid = async (): Promise<any> => {
            let data: any[] = await this.formIODefinitionService.getEpisodeNotesDataGridValues(<any>{
                ...formioMiProExtraParameters,
                patientId: this.patientId,
                organizationId: organizationId
            });
            return data.map(x => JSON.parse(x));
        }

        const componentName = "miproEpisodeNotesDatagrid";
        const componentKey = !!stage ? this.getCustomGridKeyForStageTab(json, stage, componentName) : this.getCustomGridKey(json, componentName);
        const filedName = componentKey.split(".").pop();

        let values: any[] = await getDataForEpisodeNotesGrid();
        await this.findComponentAndUpdateDefaultValue(
            json,
            componentKey,
            async () => values
        );

        this.replaceInFormDataJson(
            filedName,
            values
        )

        return json;
    }

    private async updateConsultHistDataGridV2Fields(json: any, formioMiProExtraParameters: any, stage: MultiUserFormAssociationLink = null): Promise<string> {
        if (!formioMiProExtraParameters.selectedEpisodesJson) return json;

        const getDataForConsultationHistGridV2 = async (): Promise<any> => {
            const insertedEpisodesDto = JSON.parse(formioMiProExtraParameters.selectedEpisodesJson);
            let episodes = await this.episodeService.getSelectedEpisodes(insertedEpisodesDto);

            episodes.sort((a, b) => {
                const timeA = new Date(a.updateDateTime).getTime();
                const timeB = new Date(b.updateDateTime).getTime();
                return timeB - timeA;
            });

            const mappedEpisodes = episodes.map(e => ({
                episodeDateTime: e.updateDateTime,
                episodeInfo: e.diagnoses
                    .map(diagnose =>
                        `${diagnose.diagnosisName}${
                            diagnose.diagnosisBodyPartName !== "N/A" ? ` - ${diagnose.diagnosisBodyPartName}` : ""
                        }${
                            diagnose.bodySide !== "N/A" ? ` | ${diagnose.bodySide}` : ""
                        }${
                            diagnose.diagnosisMedicalSystemName ? ` | ${diagnose.diagnosisMedicalSystemName}` : ""
                        }`
                    )
                    .join("; "),
                subEntitiesGrid: mapSubEntities(e).map(subEnt => ({
                    container: subEnt
                }))
            }));

            return mappedEpisodes;
        }

        const mapSubEntities = (episode: IEpisode): any[] => {
            let subEntities = [];

            episode.documents.forEach(document => {
                if (document.document_Files.length) {
                    if (episode.assignedClinicianUserRole == "PPS" &&
                        !document.createdByRoles &&
                        !document.createdByOrganizationName &&
                        !document.createdByFullName) {
                        document.createdByRoles = "PPS#Patient";
                        document.createdByOrganizationName = episode.organizationName;
                        document.createdByFullName = "Unknown Clinician";
                    }

                    document.document_Files.forEach(doc_file => {
                        let docRibbon = {
                            time: document.uploadTime, // Normalized time field
                            documentDateTime: document.uploadTime,
                            documentName: `${doc_file.fileDetails.fileName}${doc_file.fileDetails.extension}`,
                            documentKey: doc_file.fileDetails.key,
                            documentUrl: doc_file.fileDetails.url,
                            type: "document"
                        };
                        subEntities.push(docRibbon);
                    })
                }
            });

            if (!this.userHelperService.hasRole(CoreRole.ClinicalSafetyOfficer)) {
                episode.formInstances.forEach(form => {
                    let formRibbon = {
                        time: form.createdOn, // Normalized time field
                        formDateTime: form.createdOn,
                        formName: form.formDefinition.name,
                        documentName: `${form.fileDetails.fileName}${form.fileDetails.extension}`,
                        documentKey: form.fileDetails.key,
                        documentUrl: form.fileDetails.url,
                        type: "form"
                    };
                    subEntities.push(formRibbon);
                });
            }

            episode.prescriptions.forEach(prescription => {
                let presRibbon = {
                    time: prescription.prescriptionLines[0].prescribedDateTime, // Normalized time field
                    prescriptionDateTime: prescription.prescriptionLines[0].prescribedDateTime,
                    prescriptionData: prescription.prescriptionLines
                        .map(pl => this.prescriptionLinesService.generatePrescriptionLineText(pl)).join("<br/>"),
                    type: "prescription"
                };
                subEntities.push(presRibbon);
            });

            episode.notes.reverse().forEach(note => {
                this.noteService.addLables(note.noteFields);
                let data = "Clinical Note";

                if (note.noteTreatments && note.noteTreatments.length) {
                    data += "<br\><br\>Note Treatments:<br\>";
                    data += note.noteTreatments.map(x => x.treatmentName).join(", ");
                }

                note.noteFields = this.noteService.sortNoteFields(note.noteFields);
                if (note.noteFields && note.noteFields.length) {
                    data += "<br\><br\>";
                    note.noteFields.forEach(noteField => {
                        data += `${noteField.label}: ${noteField.value}<br\>`;
                    })
                }
                let noteRibbon = {
                    time: note.creationDate, // Normalized time field
                    noteDateTime: note.creationDate,
                    noteData: `<b>${data}</b>`,
                    type: "note",
                };
                subEntities.push(noteRibbon);
            });

            subEntities.sort((a, b) => {
                const timeA = new Date(a.time).getTime();
                const timeB = new Date(b.time).getTime();
                return timeB - timeA;
            });

            return subEntities;
        }

        const componentName = "miproConsultHistDatagridV2";
        const componentKey = !!stage ? this.getCustomGridKeyForStageTab(json, stage, componentName) : this.getCustomGridKey(json, componentName);
        const filedName = componentKey.split(".").pop();

        let values: any[] = await getDataForConsultationHistGridV2();
        await this.findComponentAndUpdateDefaultValue(
            json,
            componentKey,
            async () => values
        );

        this.replaceInFormDataJson(
            filedName,
            values
        )

        return json;
    }

    private replaceInFormDataJson(fieldName: string, values: any[]) {
        if (!this.formDataJson) return;

        this.recursiveReplace(this.formDataJson, fieldName, values);
    }

    private recursiveReplace(obj: any, fieldName: string, values: any[]) {
        if (!obj) return;

        if (Array.isArray(obj)) {
            obj.forEach(item => this.recursiveReplace(item, fieldName, values));
            return;
        }

        if (typeof obj === "object") {
            for (let key in obj) {
                if (key === fieldName) {
                    obj[key] = values;
                } else if (typeof obj[key] === "object") {
                    this.recursiveReplace(obj[key], fieldName, values);
                }
            }
        }
    }

    private getCustomGridKeyForStageTab(json: any, stage: MultiUserFormAssociationLink, customGridComponentName: string): string {
        const tabKey = stage.multiUserFormStage.innerTabKey;
        const tabComponent = this.findComponentByKey(json, tabKey);
        const customComponent = this.findComponentByType(tabComponent, customGridComponentName);
        return customComponent.key;
    }

    private getCustomGridKey(json: any, customGridComponentName: string): string {
        const customComponent = this.findComponentByType(json, customGridComponentName);
        return customComponent.key;
    }

    //This function is used for cases when the formDataJson field is null, so we still have a way to fill grids with data
    private findComponentAndUpdateDefaultValue = async (jsonObject: any, componentKey: string, defaultValueGetter: () => any): Promise<void> => {
        if (jsonObject.key === componentKey) {
            const result = await defaultValueGetter();
            jsonObject.defaultValue = result;
            jsonObject.value = result;
            return;
        }

        for (const key in jsonObject) {
            if (jsonObject[key] && typeof jsonObject[key] === "object") {
                await this.findComponentAndUpdateDefaultValue(jsonObject[key], componentKey, defaultValueGetter);
            }
        }
    }

    private findComponentByPropValue(jsonObject: any, propName: string, propValue: string): any {
        if (jsonObject[propName] === propValue) {
            return jsonObject;
        }

        if (Array.isArray(jsonObject)) {
            for (const item of jsonObject) {
                const result = this.findComponentByPropValue(item, propName, propValue);
                if (result) {
                    return result;
                }
            }
        }

        if (typeof jsonObject === "object") {
            for (const key in jsonObject) {
                if (jsonObject[key] && typeof jsonObject[key] === "object") {
                    const result = this.findComponentByPropValue(jsonObject[key], propName, propValue);
                    if (result) {
                        return result;
                    }
                }
            }
        }

        return null;
    }

    private findComponentByKey(jsonObject: any, key: string): any {
        return this.findComponentByPropValue(jsonObject, "key", key);
    }

    private findComponentByType(jsonObject: any, type: string): any {
        return this.findComponentByPropValue(jsonObject, "type", type);
    }

    onCancel() {
        this.confirmationService.confirm({
            header: "Confirm Action",
            message: "Are you sure you want to go back? All unsaved changes will be lost.",
            accept: async () => {
                this.navigateBack();
            }
        });
    }

    navigateBack() {
        const previousUrl = this.navigationHistoryService.getPreviousUrl();
        this.navigationHistoryService.removeCurrentUrl();
        if (previousUrl) {
            this.router.navigate([previousUrl.url], { queryParams: previousUrl.queryParams });
        } else if (this.returnUrl) {
            this.router.navigateByUrl(this.returnUrl);
        } else {
            this.location.back();
        }
    }
}
